import { useMemo } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Settings as SettingsIcon, Coins, Shield, Users, FileLock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Billing from "@/pages/Billing";
import Compliance from "@/pages/Compliance";
import MyData from "@/pages/MyData";
import RolePresetsSettings from "@/pages/settings/RolePresetsSettings";

const TABS = [
  { path: "billing", label: "Billing & Credits", icon: Coins, needsStaff: false },
  { path: "role-access", label: "Role Permissions", icon: Users, needsAdmin: true },
  { path: "compliance", label: "Compliance", icon: Shield, needsOrg: true },
  { path: "data-privacy", label: "Data & Privacy", icon: FileLock, needsStaff: false },
];

export default function Settings() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isStaff = user?.role && !["athlete", "demo"].includes(user.role);
  const isOrg = /trifitindia\.com$/i.test(user?.email || "") || user?.role === "owner";
  const visibleTabs = useMemo(
    () => TABS.filter((t) => (!t.needsStaff || isStaff) && (!t.needsOrg || isOrg) && (!t.needsAdmin || user?.role === "admin" || isOrg)),
    [isStaff, isOrg, user?.role],
  );
  const currentTab = visibleTabs.find((t) => pathname.includes(`/settings/${t.path}`))?.label || "Settings";

  return (
    <div>
      <header className="min-h-16 border-b border-border flex flex-wrap items-center px-4 sm:px-8 py-3 gap-3">
        <SettingsIcon className="w-5 h-5" />
        <h1 className="font-heading text-lg sm:text-xl font-black tracking-tight">Settings</h1>
        <span className="text-xs text-muted-foreground hidden md:block truncate">
          / {currentTab}
        </span>
      </header>
      <div className="p-4 sm:p-8 grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 max-w-6xl">
        <nav className="space-y-1" data-testid="settings-tabs">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.path}
                to={`/settings/${t.path}`}
                data-testid={`settings-tab-${t.path}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-foreground text-background"
                      : "hover:bg-surface text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </NavLink>
            );
          })}
        </nav>
        <div>
          <Routes>
            <Route index element={<Navigate to="billing" replace />} />
            <Route path="billing" element={<Billing />} />
            <Route path="role-access" element={user?.role === "admin" || isOrg ? <RolePresetsSettings /> : <NotAllowed />} />
            <Route path="compliance" element={isOrg ? <Compliance /> : <NotAllowed />} />
            <Route path="data-privacy" element={<MyData />} />
            <Route path="*" element={<Navigate to="billing" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function NotAllowed() {
  return (
    <div className="p-8 text-sm text-muted-foreground" data-testid="settings-not-allowed">
      You don't have access to this section.
    </div>
  );
}
